/**
 * Générateur — Résoudre des problèmes impliquant des longueurs
 * Attendu ID : 643 | Niveau : 6ème | Domaine : Grandeurs et mesures — Les longueurs
 *
 * Types générés : qcm_unique, texte, tableau_vf, texte_trou_saisie
 * Labels de référence :
 *   - Déterminer si une piste d'athlétisme est homologuée
 * Variantes estimées : ~400+
 */

import {
    randomChoice, randInt,
    makeQcm, makeSaisie, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 643;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_PISTE = 'Déterminer si une piste d\'athlétisme est homologuée';
const L_PROBLEME = 'Résoudre un problème impliquant des longueurs';

// ─── Helpers numériques ───
const arrCent = (n) => Math.round(n * 100) / 100;
const fmtFr = (n) => String(n).replace('.', ',');

// ─── Pools ───
const PRENOMS_F = [
    { nom: 'Léa', fem: true }, { nom: 'Emma', fem: true },
    { nom: 'Jade', fem: true }, { nom: 'Louise', fem: true },
    { nom: 'Alice', fem: true }, { nom: 'Chloé', fem: true },
    { nom: 'Inès', fem: true }, { nom: 'Lina', fem: true },
];
const PRENOMS_M = [
    { nom: 'Tom', fem: false }, { nom: 'Hugo', fem: false },
    { nom: 'Lucas', fem: false }, { nom: 'Nathan', fem: false },
    { nom: 'Raphaël', fem: false }, { nom: 'Louis', fem: false },
    { nom: 'Adam', fem: false }, { nom: 'Sacha', fem: false },
];
const PRENOMS = [...PRENOMS_F, ...PRENOMS_M];

// ─── SVG piste d'athlétisme ───
// Rayon MATÉRIALISÉ en pointillés dans le virage droit (revue 2026-07-30) :
// l'ancienne étiquette flottante à gauche chevauchait l'arc selon la largeur
// du nombre. L'étiquette vit désormais dans l'INFIELD (zone intérieure vide,
// ancrage à droite) : aucune collision possible, quelle que soit la valeur —
// et le pointillé montre CE QUE mesure R (même convention que svgCercle).
const svgPiste = (L, R) =>
    `<svg xmlns='http://www.w3.org/2000/svg' width='300' height='160' viewBox='0 0 300 160'>`
    + `<line x1='75' y1='20' x2='225' y2='20' stroke='currentColor' stroke-width='2'/>`
    + `<line x1='75' y1='140' x2='225' y2='140' stroke='currentColor' stroke-width='2'/>`
    + `<path d='M 75 20 A 60 60 0 0 0 75 140' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
    + `<path d='M 225 20 A 60 60 0 0 1 225 140' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
    + `<circle cx='225' cy='80' r='2.5' fill='#fb923c'/>`
    + `<line x1='225' y1='80' x2='285' y2='80' stroke='#fb923c' stroke-width='2' stroke-dasharray='4,3'/>`
    + `<text x='150' y='15' text-anchor='middle' font-size='12' fill='currentColor'>${L} m</text>`
    + `<text x='150' y='155' text-anchor='middle' font-size='12' fill='currentColor'>${L} m</text>`
    + `<text x='250' y='72' text-anchor='middle' font-size='11' fill='#fb923c'>R = ${fmtFr(R)} m</text>`
    + `</svg>`;

// ─── SVG jardin (rectangle + demi-cercle) ───
// Revue 2026-07-30 : l'étiquette « l » dupliquée qui flottait au CENTRE du
// rectangle a été retirée — la largeur n'est cotée qu'une fois, sous la
// figure ; l'élève déduit lui-même l'égalité des côtés opposés du rectangle.
// Proportions REDRESSÉES : le générateur garantit L > l, le dessin doit
// montrer un rectangle plus HAUT (côté L) que large (côté l) — l'ancien
// 120×110 contredisait les cotes à chaque tirage (doctrine 614/969 : une
// figure n'est pas à l'échelle, mais ne contredit jamais ses données).
// Le côté HAUT du rectangle n'est pas tracé (revue 2026-07-30) : il ne fait
// pas partie de la clôture — c'est le diamètre du demi-cercle, que l'élève
// doit déduire lui-même (l'énoncé le dit). La figure montre exactement le
// contour qui se calcule : deux longueurs + la largeur du bas + l'arc.
const svgJardin = (L, l) =>
    `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='220' viewBox='0 0 200 220'>`
    + `<path d='M 50 70 V 200 H 150 V 70' fill='none' stroke='currentColor' stroke-width='2'/>`
    + `<path d='M 50 70 A 50 50 0 0 1 150 70' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
    + `<text x='28' y='140' text-anchor='middle' font-size='13' fill='currentColor'>${L} m</text>`
    + `<text x='100' y='215' text-anchor='middle' font-size='13' fill='currentColor'>${l} m</text>`
    + `</svg>`;

// ─── Pool de contextes problèmes ───

// Contexte 1 : Piste d'athlétisme
const genPiste = () => {
    const L = randInt(70, 90);        // ligne droite
    const R = randInt(30, 40);        // rayon intérieur (peut être décimal)
    // Parfois rayon décimal
    const Rfinal = Math.random() < 0.4 ? R + 0.5 : R;
    const arcs = arrCent(2 * Math.PI * Rfinal);
    const droites = 2 * L;
    const perimetre = arrCent(droites + arcs);
    const homologue = perimetre >= 400 && perimetre <= 402.3;

    return {
        type: 'piste',
        L, R: Rfinal, perimetre, homologue, arcs, droites,
        svg: svgPiste(L, Rfinal),
        label: L_PISTE,
    };
};

// Contexte 2 : Clôturer un jardin (rectangle + demi-cercle)
const genJardin = () => {
    const prenom = randomChoice(PRENOMS);
    const pronom = prenom.fem ? 'elle' : 'il';
    const L = randInt(8, 18);    // longueur
    const l = randInt(5, 12);    // largeur (= diamètre du demi-cercle)
    if (L <= l) return null;

    const demiArc = arrCent(Math.PI * l / 2);
    const segments = 2 * L + l;
    const perimetre = arrCent(segments + demiArc);
    const grillage = randInt(Math.round(perimetre) - 5, Math.round(perimetre) + 10);
    const suffisant = grillage >= perimetre;

    return {
        type: 'jardin',
        prenom, pronom, L, l, perimetre, grillage, suffisant, demiArc, segments,
        svg: svgJardin(L, l),
        label: L_PROBLEME,
    };
};

// Contexte 3 : Bord de piscine (stade = rectangle + 2 demi-cercles)
const genPiscine = () => {
    const L = randInt(10, 25);   // longueur
    const l = randInt(6, 14);    // largeur (= diamètre des demi-cercles)
    if (L <= l) return null;

    const cercleFull = arrCent(Math.PI * l);
    const perimetre = arrCent(2 * L + cercleFull);

    return {
        type: 'piscine',
        L, l, perimetre, cercleFull,
        label: L_PROBLEME,
    };
};

// Contexte 4 : Ruban autour d'un objet circulaire
const genRuban = () => {
    const prenom = randomChoice(PRENOMS);
    const pronom = prenom.fem ? 'elle' : 'il';
    const D = randInt(15, 35);
    const perimetre = arrCent(Math.PI * D);
    const ruban = randInt(Math.round(perimetre) - 15, Math.round(perimetre) + 15);
    const suffisant = ruban >= perimetre;

    const objets = [
        { nom: 'gâteau', article: 'un' },
        { nom: 'tambour', article: 'un' },
        { nom: 'pot de fleurs', article: 'un' },
        { nom: 'horloge', article: 'une' },
    ];
    const objet = randomChoice(objets);

    return {
        type: 'ruban',
        prenom, pronom, D, perimetre, ruban, suffisant, objet,
        label: L_PROBLEME,
    };
};

const CONTEXTES_GEN = [genPiste, genJardin, genPiscine, genRuban];

const tirerContexte = (typeForce) => {
    const genFn = typeForce
        ? CONTEXTES_GEN.find((_, i) => ['piste', 'jardin', 'piscine', 'ruban'][i] === typeForce) || randomChoice(CONTEXTES_GEN)
        : randomChoice(CONTEXTES_GEN);
    for (let attempt = 0; attempt < 50; attempt++) {
        const ctx = genFn();
        if (ctx && ctx.perimetre >= 10) return ctx;
    }
    return genPiste(); // fallback sûr
};

// ─── Formulation des énoncés ───

/** Convertit un SVG brut en data URL + element image. */
const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

/** SVG cercle pour les contextes ruban/piscine circulaire. */
const svgCercle = (D) => {
    const W = 160, H = 160, cx = 80, cy = 80, r = 55;
    return `<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}' viewBox='0 0 ${W} ${H}'>`
        + `<circle cx='${cx}' cy='${cy}' r='${r}' fill='none' stroke='currentColor' stroke-width='2.5'/>`
        + `<line x1='${cx - r}' y1='${cy}' x2='${cx + r}' y2='${cy}' stroke='#fb923c' stroke-width='2' stroke-dasharray='4,3'/>`
        + `<text x='${cx}' y='${cy - 8}' text-anchor='middle' font-size='12' fill='#fb923c' font-family='system-ui,sans-serif'>D = ${D} cm</text>`
        + `</svg>`;
};

/** SVG stade (piscine) = 2 lignes droites + 2 demi-cercles.
 *  Largeur MATÉRIALISÉE en pointillés (revue 2026-07-30) : le diamètre du
 *  demi-cercle droit est tracé en tirets orange, l'étiquette plaquée contre,
 *  à mi-hauteur, dans le corps vide du stade — aucune collision possible
 *  (l'ancienne étiquette flottait à gauche, contre l'arc, même défaut que le
 *  R de la piste). L'élève VOIT que la largeur est le diamètre des virages. */
const svgStade = (L, l) => {
    return `<svg xmlns='http://www.w3.org/2000/svg' width='280' height='140' viewBox='0 0 280 140'>`
        + `<line x1='70' y1='20' x2='210' y2='20' stroke='currentColor' stroke-width='2'/>`
        + `<line x1='70' y1='120' x2='210' y2='120' stroke='currentColor' stroke-width='2'/>`
        + `<path d='M 70 20 A 50 50 0 0 0 70 120' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
        + `<path d='M 210 20 A 50 50 0 0 1 210 120' fill='none' stroke='#fb923c' stroke-width='2.5'/>`
        + `<line x1='210' y1='20' x2='210' y2='120' stroke='#fb923c' stroke-width='2' stroke-dasharray='4,3'/>`
        + `<text x='140' y='15' text-anchor='middle' font-size='12' fill='currentColor' font-family='system-ui,sans-serif'>${L} m</text>`
        + `<text x='140' y='135' text-anchor='middle' font-size='12' fill='currentColor' font-family='system-ui,sans-serif'>${L} m</text>`
        + `<text x='202' y='74' text-anchor='end' font-size='12' fill='#fb923c' font-family='system-ui,sans-serif'>${l} m</text>`
        + `</svg>`;
};

const enonceQcmPiste = (ctx) => {
    const texte = `<p>Une piste d'athlétisme est composée de deux lignes droites de ${ctx.L} m chacune et de deux demi-cercles de rayon intérieur ${fmtFr(ctx.R)} m.</p>{{element:0}}`
        + `<p>Un tour complet intérieur doit mesurer entre 400 m et 402,3 m pour être homologué. Cette piste est-elle homologuée\u00a0?</p>`;

    // Conclusion DÉRIVÉE de la valeur affichée : « Oui » ssi elle est dans la
    // fenêtre d'homologation. Un choix ne peut plus se contredire lui-même
    // (revue 2026-07-30 : l'ancien piège « Oui, car ≈ 2L+6R m » affichait une
    // valeur hors fenêtre dans 97,3 % des cas — auto-réfutant à la lecture ;
    // et π ≈ 3 risquait la collision avec les élèves calculant à 3,14).
    const libelle = (v) => `${v >= 400 && v <= 402.3 ? 'Oui' : 'Non'}, car le tour mesure environ ${fmtFr(v)} m.`;

    const correct = libelle(ctx.perimetre);

    // Pièges = productions exactes d'erreurs nommées (identiques dans tous les
    // cas ; valeurs 2 à 2 distinctes par construction, écarts R, (π−1)R, πR) :
    //   · somme des 3 valeurs VISIBLES sur la figure (aucune formule) : 2L + R
    //   · un seul demi-cercle compté (formule à moitié)              : 2L + πR
    //   · lignes droites seules (virages oubliés)                    : 2L
    const pieges = [
        libelle(arrCent(ctx.droites + ctx.R)),
        libelle(arrCent(ctx.droites + Math.PI * ctx.R)),
        libelle(ctx.droites),
    ];

    return { texte, correct, pieges };
};

// ─── Générateurs par type ───

const genPisteQcm = () => {
    const ctx = tirerContexte('piste');
    const { texte, correct, pieges } = enonceQcmPiste(ctx);
    const choix = [correct, ...pieges];

    const payload = makeQcm(choix, correct, texte, { ...opts(L_PISTE), elements_additionnels: svgToElement(ctx.svg) });
    // Calculatrice AUTORISÉE sur CE format (revue 2026-07-30) : départager les
    // choix exige 2πR, la démarche est l'objet — pas la technique opératoire.
    // Posé dans `contenu` (et pas _meta, strippé au POST) pour survivre à la
    // sauvegarde en banque et arriver intact côté élève. Marqueur par FORMAT :
    // les autres formats du gen ne le portent pas (décision au cas par cas).
    // sousType STRUCTUREL : le contexte porte deja un `type`, on le fait
    // voyager pour router le coup de pouce sans dependre des mots de
    // l'enonce (revue geometrie, 2026-08-05).
    payload.contenu = { ...payload.contenu, calculatrice: true, sousType: ctx.type };
    return payload;
};

const genProblemeSaisie = () => {
    const ctx = tirerContexte();

    let texte, suffixe, svg;

    if (ctx.type === 'piste') {
        texte = `<p>Une piste d'athlétisme est composée de deux lignes droites de ${ctx.L} m chacune et de deux demi-cercles de rayon intérieur ${fmtFr(ctx.R)} m. Calcule la longueur d'un tour complet. Arrondis au <strong>centième</strong> de mètre.</p>{{element:0}}`;
        suffixe = 'm';
        svg = ctx.svg;
    } else if (ctx.type === 'jardin') {
        texte = `<p>${ctx.prenom.nom} veut clôturer son jardin en forme de rectangle surmonté d'un demi-cercle. Le rectangle mesure ${ctx.L} m de long et ${ctx.l} m de large. Le demi-cercle a pour diamètre la largeur du rectangle. Combien de mètres de grillage faut-il au minimum\u00a0? Arrondis au <strong>centième</strong> de mètre.</p>{{element:0}}`;
        suffixe = 'm';
        svg = ctx.svg;
    } else if (ctx.type === 'piscine') {
        texte = `<p>Une piscine en forme de stade mesure ${ctx.L} m de long et ${ctx.l} m de large (2 demi-cercles aux extrémités). On veut poser une margelle*. Quelle longueur de margelle faut-il commander pour en faire le tour\u00a0? Arrondis au <strong>centième</strong> de mètre.</p>{{element:0}}`;
        // « margelle* » (revue 2026-07-30) : produit vendu au mètre linéaire,
        // cohérent avec un périmètre (« bordure » sonnait trottoir, carreler
        // serait une surface). Astérisque + note = obstacle lexical levé en 6e.
        texte += `<p><em>* margelle : la bordure en pierre qui fait le tour de la piscine.</em></p>`;
        suffixe = 'm';
        svg = svgStade(ctx.L, ctx.l);
    } else {
        // ruban
        texte = `<p>${ctx.prenom.nom} veut entourer ${ctx.objet.article} ${ctx.objet.nom} circulaire de diamètre ${ctx.D} cm avec un ruban. Quelle longueur de ruban faut-il au minimum\u00a0? Arrondis au <strong>centième</strong> de cm.</p>{{element:0}}`;
        suffixe = 'cm';
        svg = svgCercle(ctx.D);
    }

    const payload = makeSaisie(fmtFr(ctx.perimetre), texte, { ...opts(ctx.label), suffixe, elements_additionnels: svgToElement(svg) });
    // Calculatrice AUTORISÉE (revue 2026-07-31) : périmètre avec π arrondi au
    // centième — la démarche est l'objet, pas la technique opératoire.
    // sousType STRUCTUREL : le contexte porte deja un `type`, on le fait
    // voyager pour router le coup de pouce sans dependre des mots de
    // l'enonce (revue geometrie, 2026-08-05).
    payload.contenu = { ...payload.contenu, calculatrice: true, sousType: ctx.type };
    payload._meta.generatorKey = 'probleme_longueurs';
    return payload;
};

const genPisteSaisieTrou = () => {
    const ctx = tirerContexte('piste');
    const concl = ctx.homologue ? 'oui' : 'non';

    // Ligne 1 = DONNÉES en clair (revue 2026-07-31) : les anciens trous de
    // recopie sont pré-remplis — 9 trous étaient trop denses, on passe à 5.
    // Ligne 4 : la somme est écrite EN MOTS — deux trous de recopie autour d'un
    // « + » formaient une paire COMMUTATIVE (176 + 216,77 = 216,77 + 176) que
    // le correcteur positionnel comptait faux dans un sens. Règle : jamais deux
    // trous interchangeables autour d'un opérateur commutatif.
    const phrase = `La piste est composée de 2 lignes droites de ${ctx.L} m et de 2 demi-cercles de rayon ${fmtFr(ctx.R)} m.\n`
        + `Longueur des 2 lignes droites = 2 × ${ctx.L} = {{${ctx.droites}}} m.\n`
        + `Longueur des 2 demi-cercles = 2 × π × {{${fmtFr(ctx.R)}}} ≈ {{${fmtFr(ctx.arcs)}}} m.\n`
        + `Tour complet = longueur des droites + longueur des demi-cercles ≈ {{${fmtFr(ctx.perimetre)}}} m.\n`
        + `La piste est-elle homologuée ? {{${concl}}}`;

    const svgDataUrl = `data:image/svg+xml,${encodeURIComponent(svgPiste(ctx.L, ctx.R))}`;
    const texte = '<p>Une piste d\'athlétisme est <strong>homologuée</strong> si un tour complet intérieur mesure entre 400 m et 402,3 m. Complète le raisonnement. Les valeurs avec ≈ sont arrondies au <strong>centième</strong>.</p>{{element:0}}';
    const elAdd = { version: 1, elements: [{ type: 'image', config: { imageUrl: svgDataUrl, maxWidth: 'compact' } }] };
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_PISTE), mode: 'saisie', elements_additionnels: elAdd });
    // Calculatrice AUTORISÉE (revue 2026-07-31) : 2 × π × R au centième dans
    // les trous — même décision que les deux autres formats du gen.
    // sousType STRUCTUREL : le contexte porte deja un `type`, on le fait
    // voyager pour router le coup de pouce sans dependre des mots de
    // l'enonce (revue geometrie, 2026-08-05).
    payload.contenu = { ...payload.contenu, calculatrice: true, sousType: ctx.type };
    payload._meta.generatorKey = 'probleme_longueurs';
    return payload;
};

// ─── Registre ───
const GENERATORS = {
    probleme_longueurs_qcm_unique: genPisteQcm,
    probleme_longueurs_texte: genProblemeSaisie,
    probleme_longueurs_texte_trou_saisie: genPisteSaisieTrou,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'probleme_longueurs', label: L_PROBLEME, formatsList: buildFormats('probleme_longueurs') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
